/**
 * The settings screen.
 *
 * Reads the registry, groups what it finds by section, and asks the control registry to build each
 * row. It knows no knob by name, no control by shape and no quantity by unit: a knob that arrived
 * after this file was written renders exactly the same way as the one that was here first.
 *
 * Section order is registry order, and a section appears where its first knob does. That is the
 * same rule the HUD frame uses for anchors, and it is why nothing here holds a list of sections:
 * a second list is a second order to keep in step with the first.
 *
 * There is no Apply button and no Cancel. Every knob applies as it moves and is written to the
 * character, so the only verb on this screen is Close. A settings screen with a Cancel button owes
 * the player an undo for a value they have already been feeling for a minute.
 *
 * It opens over whatever raised it rather than replacing it: from the menu, the menu is still
 * behind it; from a paused game, the game is still standing there, un-torn-down, turning at
 * whatever the slider now says.
 *
 * The save panel is handed in rather than built here, on the same terms the main menu takes it: it
 * is a component with a root, so this screen mounts one instead of growing a second copy of the
 * export, the confirm and the backup.
 */

import { createSettingRow } from '@/ui/settings/settingRow';
import { el } from '@/ui/dom';
import { injectSettingsStyles } from '@/ui/settings/settingsStyles';
import {
  SETTINGS_CLOSE,
  SETTINGS_SAVE_SECTION,
  SETTINGS_TITLE,
  SETTING_SECTION_TITLES,
} from '@/text/settingsText';
import type {
  SettingDefinition,
  SettingSection,
  SettingValue,
} from '@/ui/settings/settingContract';

export interface SettingsScreenHandlers {
  /** Every knob to show, in the order to show it. Callers pass `SETTINGS`. */
  readonly definitions: readonly SettingDefinition[];
  /** What a knob is on right now, asked once per row as it is built. */
  readonly value: (definition: SettingDefinition) => SettingValue;
  /** The player is moving one. The caller applies it and writes nothing. */
  readonly onChange: (definition: SettingDefinition, value: SettingValue) => void;
  /** The player let go of one. The caller applies it and keeps it. */
  readonly onCommit: (definition: SettingDefinition, value: SettingValue) => void;
  readonly onClose: () => void;
  /** The export and import panel, built by the shell. Omitted mid-game, where it is a footgun. */
  readonly saveTransfer?: HTMLElement | null;
}

export interface SettingsScreen {
  dispose(): void;
}

export function createSettingsScreen(
  container: HTMLElement,
  handlers: SettingsScreenHandlers,
): SettingsScreen {
  const removeStyles = injectSettingsStyles();

  const root = el('div', 'settings');
  const page = el('div', 'settings-page');
  root.append(page);

  const close = el('button', 'settings-close', SETTINGS_CLOSE);
  close.type = 'button';
  close.addEventListener('click', handlers.onClose);

  const head = el('div', 'settings-head');
  head.append(el('h1', 'settings-title', SETTINGS_TITLE), close);
  page.append(head);

  const rows = handlers.definitions.map((definition) =>
    createSettingRow({
      definition,
      value: handlers.value(definition),
      onChange: (value: SettingValue): void => {
        handlers.onChange(definition, value);
      },
      onCommit: (value: SettingValue): void => {
        handlers.onCommit(definition, value);
      },
    }),
  );

  // One group per section, in the order the sections' first knobs appear. Built by walking the
  // rows once rather than by asking for every section that exists: a section nothing registers to
  // is a heading over an empty box.
  const groups = new Map<SettingSection, HTMLElement>();
  for (const [index, definition] of handlers.definitions.entries()) {
    let group = groups.get(definition.section);
    if (group === undefined) {
      group = el('div', 'settings-group');
      group.append(el('div', 'settings-section', SETTING_SECTION_TITLES[definition.section]));
      groups.set(definition.section, group);
      page.append(group);
    }
    const row = rows[index];
    if (row !== undefined) group.append(row.root);
  }

  // Last, and in its own box, because it is the one thing on this screen that is not a knob: it
  // does not apply, it writes a file.
  const saveTransfer = handlers.saveTransfer ?? null;
  if (saveTransfer !== null) {
    const group = el('div', 'settings-group');
    group.append(el('div', 'settings-section', SETTINGS_SAVE_SECTION), saveTransfer);
    page.append(group);
  }

  container.append(root);
  // The screen was raised by a press or a key, so something already had focus somewhere behind it.
  // Landing on Close is what makes the way out the first thing a keyboard finds.
  close.focus();

  return {
    dispose(): void {
      for (const row of rows) row.dispose();
      root.remove();
      removeStyles();
    },
  };
}
